import { useCallback } from "react";
import { useCanAccess } from "./useCanAccess";

/**
 * Priority-ordered list of routes used to determine the "home" page.
 * The first route for which canAccess() returns true will be used as home.
 * Routes with broader access (e.g. '/') come first, followed by more specific ones.
 */
const HOME_ROUTE_PRIORITY: string[] = [
    "/",
    "/feed",
    "/peta-inspeksi",
    "/mill-dashboard",
    "/estate-dashboard",
    "/estate-map",
    "/hotspots",
    "/water-level",
    "/harvesters-performance",
    "/operational/harvest-monitoring",
    "/operational/mutu-ancak",
    "/operational/mutu-buah",
    "/operational/rkh",
    "/operational/taksasi",
    "/operational/upkeep-monitoring",
    "/operational/production-monitoring",
];

const FALLBACK_ROUTE = "/403";

/**
 * Returns a function that resolves the appropriate "home" route
 * based on the current user's RBAC permissions.
 *
 * Usage:
 *   const getHomeRoute = useHomeRoute();
 *   const homeRoute = getHomeRoute(); // e.g. "/operational/harvest-monitoring"
 */
export function useHomeRoute() {
    const { canAccess } = useCanAccess();

    const getHomeRoute = useCallback((): string => {
        for (const route of HOME_ROUTE_PRIORITY) {
            if (canAccess(route)) {
                return route;
            }
        }
        return FALLBACK_ROUTE;
    }, [canAccess]);

    return getHomeRoute;
}
